import { useEffect, useRef, useState } from "react";
import {
  AlertCircle,
  ArrowRight,
  Check,
  FileUp,
  Link2,
  Loader2,
  PencilLine,
  Sparkles
} from "lucide-react";
import { previewSpec } from "../../services/inHouseService";
import { fileProblem } from "./parts";
import OrbitLoader from "../ui/OrbitLoader";

// A real public spec, so an admin can try the whole flow without their own docs.
const PETSTORE_URL = "https://petstore3.swagger.io/api/v3/openapi.json";

const AUTH_LABEL = {
  oauth_client_credentials: "Sign-in with client ID & secret",
  api_key: "API key",
  bearer: "Access token",
  none: "No sign-in"
};

/**
 * First screen of the builder. One input takes a docs link or a spec file;
 * the server reads it and nothing is saved until it succeeds.
 */
export default function StartScreen({ onImported, onScratch }) {
  const [url, setUrl] = useState("");
  const [dragging, setDragging] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null); // { source, preview }
  const fileRef = useRef(null);

  const read = async (source) => {
    if (busy) return;
    setBusy(true);
    setError("");
    const response = await previewSpec(source);
    setBusy(false);
    if (!response.success) {
      setError(response.message);
      return;
    }
    setResult({ source, preview: response.data });
  };

  const readFile = async (file) => {
    if (!file) return;
    const error = fileProblem(file);
    if (error) {
      setError(error);
      return;
    }
    let content;
    try {
      content = await file.text();
    } catch {
      setError("Orbit couldn't open that file. Try saving it again and re-uploading.");
      return;
    }
    if (!content.trim()) {
      setError("That file is empty.");
      return;
    }
    read({ content });
  };

  // Setup can still fail after the file was read (e.g. the name is taken); go back
  // to this screen with the reason instead of leaving the progress card spinning.
  const finish = async () => {
    const outcome = await onImported(result.source, result.preview);
    if (outcome && !outcome.success) {
      setResult(null);
      setError(outcome.message);
    }
  };

  if (result) {
    return <ReadingSummary preview={result.preview} onCreate={finish} onBack={() => setResult(null)} />;
  }

  return (
    <div className="mx-auto max-w-2xl px-6 py-14">
      <div className="mb-8 text-center">
        <div className="mx-auto mb-5 flex h-12 w-12 items-center justify-center rounded-2xl bg-blue-50">
          <Sparkles className="h-6 w-6 text-blue-600" />
        </div>
        <h1 className="text-3xl font-semibold tracking-tight text-gray-900">
          Connect your own API
        </h1>
        <p className="mt-2 text-gray-600">
          Paste a link to your API docs and Orbit sets up the rest. You can
          review everything before it goes live.
        </p>
      </div>

      <div
        onDragOver={(e) => {
          e.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragging(false);
          if (e.dataTransfer.files?.length > 1) {
            setError("Drop one file at a time.");
            return;
          }
          readFile(e.dataTransfer.files?.[0]);
        }}
        className={`rounded-2xl border bg-white p-2 shadow-sm transition ${
          dragging ? "border-blue-600 ring-4 ring-blue-100" : "border-gray-200"
        }`}
      >
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (url.trim()) read({ url: url.trim() });
          }}
          className="flex items-center gap-2"
        >
          <Link2 className="ml-3 h-5 w-5 shrink-0 text-gray-400" />
          <input
            autoFocus
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="https://api.yourcompany.com/openapi.json"
            aria-label="Link to your API docs"
            className="min-w-0 flex-1 bg-transparent py-3 text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none"
          />
          <button
            type="submit"
            disabled={!url.trim() || busy}
            className="flex items-center gap-1.5 rounded-xl bg-blue-600 px-4 py-2.5 text-sm font-medium text-white transition hover:bg-blue-700 disabled:bg-gray-200 disabled:text-gray-500"
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
            Read my API
            {!busy && <ArrowRight className="h-4 w-4" />}
          </button>
        </form>
        <div className="mt-1 flex items-center justify-between border-t border-gray-100 px-3 pb-1 pt-2.5 text-xs text-gray-500">
          <span>OpenAPI 3.x or Swagger 2.0 · JSON or YAML · up to 5 MB</span>
          <button
            type="button"
            disabled={busy}
            onClick={() => fileRef.current?.click()}
            className="flex items-center gap-1 font-medium text-blue-600 hover:text-blue-700 disabled:opacity-50"
          >
            <FileUp className="h-3.5 w-3.5" />
            {busy ? "Reading…" : "or drop / upload a file"}
          </button>
          <input
            ref={fileRef}
            type="file"
            accept=".json,.yaml,.yml,application/json,application/yaml,text/yaml"
            className="hidden"
            onChange={(e) => {
              readFile(e.target.files?.[0]);
              // Reset, so choosing the same file again (after fixing it) still fires.
              e.target.value = "";
            }}
          />
        </div>
      </div>

      {error && (
        <p className="mt-3 flex items-start gap-2 text-sm text-rose-600">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
          {error}
        </p>
      )}

      <div className="my-8 flex items-center gap-3 text-xs text-gray-400">
        <div className="h-px flex-1 bg-gray-200" />
        No docs?
        <div className="h-px flex-1 bg-gray-200" />
      </div>
      <button
        type="button"
        onClick={() => onScratch()}
        className="group flex w-full items-center gap-4 rounded-2xl border border-gray-200 bg-white p-4 text-left transition hover:border-gray-300 hover:shadow-sm"
      >
        <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-gray-100">
          <PencilLine className="h-5 w-5 text-gray-700" />
        </div>
        <div className="flex-1">
          <p className="text-sm font-medium text-gray-900">Set it up by hand</p>
          <p className="text-sm text-gray-500">
            Add the address, how to sign in, and the calls you need — one at a
            time.
          </p>
        </div>
        <ArrowRight className="h-4 w-4 text-gray-400 transition group-hover:translate-x-0.5" />
      </button>

      <p className="mt-8 text-center text-sm text-gray-500">
        Just exploring?{" "}
        <button
          type="button"
          disabled={busy}
          onClick={() => read({ url: PETSTORE_URL })}
          className="font-medium text-blue-600 hover:text-blue-700"
        >
          Try it with the public Swagger Petstore
        </button>
      </p>
    </div>
  );
}

/**
 * Short real summary of what was found, so the import doesn't feel like a black box.
 * Nothing is created until the admin confirms, so reading a spec to look at it is free.
 */
function ReadingSummary({ preview, onCreate, onBack }) {
  const usable = preview.operations.filter((o) => !o.skipped_reason).length;
  const lines = [
    preview.title ? `Opened “${preview.title}”` : "Opened the file",
    usable
      ? `Found ${usable} call${usable === 1 ? "" : "s"} you can use`
      : "Found no calls Orbit can import yet — you can add them by hand",
    preview.base_url ? `Server: ${preview.base_url}` : "No server address — you'll add it next",
    preview.auth
      ? `Sign-in method: ${AUTH_LABEL[preview.auth.kind]}`
      : "Sign-in method: choose it next"
  ];
  const [shown, setShown] = useState(0);
  const [creating, setCreating] = useState(false);
  const allShown = shown >= lines.length;

  useEffect(() => {
    if (shown >= lines.length) return undefined;
    const t = setTimeout(() => setShown((n) => n + 1), 350);
    return () => clearTimeout(t);
  }, [shown, lines.length]);

  const create = async () => {
    setCreating(true);
    // On failure the start screen replaces this card with the reason.
    await onCreate();
  };

  return (
    <div className="mx-auto max-w-md px-6 py-24">
      <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
        <div className="mb-5 flex items-center gap-3">
          {!allShown || creating ? (
            <OrbitLoader size={24} label="Importing" />
          ) : (
            <Check className="h-5 w-5 text-emerald-600" />
          )}
          <p className="font-medium text-gray-900">
            {creating ? "Setting up your API…" : allShown ? "Here's what Orbit found" : "Reading your API…"}
          </p>
        </div>
        <ul className="space-y-3">
          {lines.map((line, i) => (
            <li
              key={line}
              className={`flex items-start gap-3 text-sm transition-all duration-300 ${
                i < shown ? "opacity-100" : "translate-y-1 opacity-0"
              }`}
            >
              <span className="mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-emerald-100">
                <Check className="h-3 w-3 text-emerald-700" />
              </span>
              <span className="break-all text-gray-700">{line}</span>
            </li>
          ))}
        </ul>
        {allShown && (
          <div className="mt-6 flex justify-end gap-2">
            <button
              type="button"
              disabled={creating}
              onClick={onBack}
              className="rounded-lg px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 disabled:opacity-50"
            >
              Back
            </button>
            <button
              type="button"
              disabled={creating}
              onClick={create}
              className="flex items-center gap-1.5 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:bg-gray-200 disabled:text-gray-500"
            >
              Create integration
              <ArrowRight className="h-4 w-4" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
